import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import GoogleMapReact from 'google-map-react';
import { MyThunkDispatch, RootState } from 'store';

import { addressesRequest, addressesSelect, addressesSuccessful } from 'action-creators/addresses';
import Header from 'components/AppBar';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GoogleMapsWrapper } from 'utils/googlemaps';

import CardItem from './components/Card';
import Search from './components/Search';
import {
  Container,
  Content,
  ContainerResult,
  HeaderContainer,
  HeaderTitle,
  Info,
  CardList,
  SearchContainer,
  CancelButton,
  MapBox,
} from './styles';

const GOOGLE_MAPS_API_KEY = `${ENV.API_KEYS.GOOGLE_MAPS_API_KEY}`;
const TIMEOUT_SEARCH = 1000;
let gmapsWrapper: GoogleMapsWrapper;

const CompanyBillingAddressSearch = () => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const [keyword, setKeyword] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<number>();
  const dispatch: MyThunkDispatch = useDispatch();
  const [gMapsReady, setGMapsReady] = useState(false);

  const { company } = useSelector((state: RootState) => state.companyBilling);
  const { lastSearch, edit } = useSelector((state: RootState) => state.addresses);
  const [results, setResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);

  const doSearch = useCallback(
    async (value) => {
      setKeyword(value);
      dispatch(addressesRequest());
      try {
        const res = await gmapsWrapper.autocompleteSearch(value);
        dispatch(addressesSuccessful(value, res));
        setResults(res);
        setShowResults(true);
      } catch (error) {
        setResults([]);
        setShowResults(true);
      }
    },
    [dispatch],
  );

  const handleSearch = (value: string) => {
    if (gMapsReady && value.trim().length > 2 && value !== keyword) {
      let timeout = searchTimeout;
      clearTimeout(timeout);
      timeout = window.setTimeout(async () => {
        doSearch(value);
      }, TIMEOUT_SEARCH);
      setSearchTimeout(timeout);
    }
  };

  const loadedGoogleApi = useCallback((mapObj, mapsObj) => {
    setGMapsReady(true);
    gmapsWrapper = new GoogleMapsWrapper(mapObj, mapsObj);
  }, []);

  const handleSelect = async (place: google.maps.places.AutocompletePrediction) => {
    setShowResults(false);
    await dispatch(addressesSelect(place));
    setTimeout(() => {
      history.push('/companyBillingMapEdit', history.location.state);
    }, 500);
  };

  const handleNotFound = async () => {
    history.push('/customerSupport', { channelsOpen: true });
  };

  return (
    <Container>
      <ContainerResult $empty={!showResults}>
        <Desktop>
          <ScreenHeader title={t('billing.search.title')} />
        </Desktop>

        <Mobile>
          <Header
            goBack={() => {
              history.push('/companyBilling', history.location.state);
            }}
          >
            <HeaderContainer>
              <HeaderTitle>{t('billing.search.title')}</HeaderTitle>
            </HeaderContainer>
          </Header>
        </Mobile>

        <Content>
          <SearchContainer>
            <Search
              show={true}
              onSearch={handleSearch}
              initialValue={gMapsReady ? edit : ''}
              disabled={!gMapsReady ? 'disabled' : ''}
              fullWidth
            />
            <CancelButton
              onClick={() => {
                history.push('/companyBilling', history.location.state);
              }}
            >
              {t('billing.search.buttonCancel')}
            </CancelButton>
          </SearchContainer>

          {showResults ? (
            <CardList>
              {lastSearch && lastSearch?.description && (lastSearch?.description !== keyword || !showResults) && (
                <CardItem
                  icon="repeat"
                  name={lastSearch?.description}
                  address={lastSearch?.structured_formatting.main_text ?? ''}
                  onSelect={() => handleSelect(lastSearch)}
                />
              )}
              {results && results.length > 0
                ? results.map((place, idx) => (
                    <CardItem
                      key={idx}
                      icon="location"
                      name={place.description || ''}
                      address={place.structured_formatting.main_text ?? ''}
                      onSelect={() => handleSelect(place)}
                    />
                  ))
                : null}
              <CardItem
                icon="place"
                name={t('billing.search.notFoundTitle')}
                address={t('billing.search.notFoundText')}
                onSelect={handleNotFound}
              />
            </CardList>
          ) : (
            <Info>{t('billing.search.info', { country: t(`countries.${company?.countryId}`) })}</Info>
          )}
        </Content>
      </ContainerResult>

      <MapBox>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: GOOGLE_MAPS_API_KEY,
            language: t('locale').substring(0, 2),
            region: t('locale').substring(3),
            libraries: ['places'],
          }}
          defaultCenter={{ lat: 0, lng: 0 }}
          defaultZoom={10}
          yesIWantToUseGoogleMapApiInternals={true}
          options={{
            fullscreenControl: false,
            zoomControl: false,
          }}
          onGoogleApiLoaded={({ map, maps }) => loadedGoogleApi(map, maps)}
        />
      </MapBox>
    </Container>
  );
};

export default CompanyBillingAddressSearch;
